import api, {
  MutationRequestOptions,
  getMutationRequestConfig,
} from '../utils/axiosConfig'
import type {
  PaymentMerchant,
  PaymentMerchantDraftValidationPayload,
  PaymentMerchantDraftValidationResult,
  PaymentMerchantFormData,
  PaymentMerchantValidationRequest,
  PaymentMerchantValidationResult,
} from '../types/payment'

export type {
  PaymentMerchant,
  PaymentMerchantDraftValidationPayload,
  PaymentMerchantDraftValidationResult,
  PaymentMerchantFormData,
  PaymentMerchantValidationRequest,
  PaymentMerchantValidationResult,
} from '../types/payment'

export const getPaymentMerchants = async (
  channelCode?: string,
): Promise<PaymentMerchant[]> => {
  const response = await api.get<PaymentMerchant[]>('/payments/admin/merchants', {
    params: channelCode ? { channel_code: channelCode } : undefined,
  })
  return response.data
}

export const getPaymentMerchant = async (merchantId: number): Promise<PaymentMerchant> => {
  const response = await api.get<PaymentMerchant>(`/payments/admin/merchants/${merchantId}`)
  return response.data
}

export const createPaymentMerchant = async (
  payload: PaymentMerchantFormData & { merchant_secret: string; validation_token: string },
  options?: MutationRequestOptions,
): Promise<PaymentMerchant> => {
  const response = await api.post<PaymentMerchant>(
    '/payments/admin/merchants',
    payload,
    getMutationRequestConfig(options),
  )
  return response.data
}

export const updatePaymentMerchant = async (
  merchantId: number,
  payload: Partial<PaymentMerchantFormData> & { merchant_secret?: string; validation_token?: string },
  options?: MutationRequestOptions,
): Promise<PaymentMerchant> => {
  const response = await api.put<PaymentMerchant>(
    `/payments/admin/merchants/${merchantId}`,
    payload,
    getMutationRequestConfig(options),
  )
  return response.data
}

export const validatePaymentMerchantDraft = async (
  payload: PaymentMerchantDraftValidationPayload,
): Promise<PaymentMerchantDraftValidationResult> => {
  const response = await api.post<PaymentMerchantDraftValidationResult>(
    '/payments/admin/merchants/validate-config',
    payload,
    getMutationRequestConfig({
      preventDuplicate: false, // mutation-policy: allow-concurrent validation requests
    }),
  )
  return response.data
}

export const validatePaymentMerchant = async (
  merchantId: number,
  payload?: PaymentMerchantValidationRequest,
): Promise<PaymentMerchantValidationResult> => {
  const response = await api.post<PaymentMerchantValidationResult>(
    `/payments/admin/merchants/${merchantId}/validate`,
    payload ?? {},
    getMutationRequestConfig({
      preventDuplicate: false, // mutation-policy: allow-concurrent validation requests
    }),
  )
  return response.data
}

export const setPaymentMerchantFallbackDefault = async (
  merchantId: number,
  options?: MutationRequestOptions,
): Promise<PaymentMerchant> => {
  const response = await api.post<PaymentMerchant>(
    `/payments/admin/merchants/${merchantId}/set-fallback-default`,
    undefined,
    getMutationRequestConfig(options),
  )
  return response.data
}

export const activatePaymentMerchant = async (
  merchantId: number,
  options?: MutationRequestOptions,
): Promise<PaymentMerchant> => {
  const response = await api.post<PaymentMerchant>(
    `/payments/admin/merchants/${merchantId}/activate`,
    undefined,
    getMutationRequestConfig(options),
  )
  return response.data
}

export const deactivatePaymentMerchant = async (
  merchantId: number,
  options?: MutationRequestOptions,
): Promise<PaymentMerchant> => {
  const response = await api.post<PaymentMerchant>(
    `/payments/admin/merchants/${merchantId}/deactivate`,
    undefined,
    getMutationRequestConfig(options),
  )
  return response.data
}

export const deletePaymentMerchant = async (
  merchantId: number,
  options?: MutationRequestOptions,
): Promise<void> => {
  await api.delete(
    `/payments/admin/merchants/${merchantId}`,
    getMutationRequestConfig(options),
  )
}
